import type { intelligence } from '@cwos/api-client';
import { AutomationCard } from './AutomationCard.js';
import { ChartResult } from './ChartResult.js';
import { DashboardPreviewCard } from './DashboardPreviewCard.js';
import { KpiResult } from './KpiResult.js';
import { RecordCards } from './RecordCards.js';
import { TableResult } from './TableResult.js';
import type { ResultAction } from './resultActions.js';

/**
 * The single hand-off point: a turn's typed result objects, rendered in the
 * order the server sent them.
 *
 * This is the ONLY thing the Intelligence screen has to call to gain rich
 * responses, which is deliberate — the workspace's layout is another lane's,
 * and a screen that had to switch on six payload kinds inline would be a
 * screen two lanes edit for one reason.
 *
 * ORDER IS THE SERVER'S. The array is rendered as received. Sorting it here —
 * KPIs first, tables last — would be this component deciding which part of an
 * answer matters most, which is a judgement it does not have the payload to
 * make.
 *
 * ABSENT RENDERS NOTHING. No results, an empty array, or the field the route
 * does not serve yet: all three produce no DOM at all. Not a skeleton and not
 * an "no data" panel — an answer that carried no result objects is not an
 * answer that is missing something, and furniture announcing an absence is
 * furniture that will be there on every turn for the rest of the product's
 * life.
 *
 * UNKNOWN KINDS ARE DROPPED, NOT GUESSED. The `default` arm renders nothing:
 * a client that met a newer result kind must not improvise a rendering for a
 * payload whose meaning it does not have. Dropping it is visible (the object
 * is simply not there) whereas a generic "unsupported block" card would be a
 * permanent apology in the middle of an answer.
 */
export function ResultBlocks({
  results,
  onAction,
}: {
  results: readonly intelligence.IntelligenceResult[] | null | undefined;
  /**
   * What a card's action button asks for — [Save to Dashboards], [Edit],
   * [Review automation]. See `resultActions.ts`: an action is a REQUEST in the
   * product's vocabulary, which the surface turns into the next turn. It
   * executes nothing and authorizes nothing.
   *
   * Optional, and the cards draw no buttons without it: a surface that has not
   * wired its turn mutation to this yet still renders every result correctly,
   * rather than showing controls that quietly do nothing.
   */
  onAction?: (action: ResultAction) => void;
}) {
  if (results === null || results === undefined || results.length === 0) return null;

  return (
    <div className="rn-col" style={{ gap: 'var(--space-12)' }}>
      {results.map((result, index) => (
        <ResultBlock key={keyFor(result, index)} result={result} onAction={onAction} />
      ))}
    </div>
  );
}

/** One block. Exported so a single payload can be rendered without an array. */
export function ResultBlock({
  result,
  onAction,
}: {
  result: intelligence.IntelligenceResult | null | undefined;
  onAction?: (action: ResultAction) => void;
}) {
  if (result === null || result === undefined) return null;
  switch (result.kind) {
    case 'kpi':
      return <KpiResult kpi={result} />;
    case 'table':
      return <TableResult table={result} />;
    case 'chart':
      return <ChartResult chart={result} />;
    case 'records':
      return <RecordCards records={result} />;
    case 'automation':
      return (
        <AutomationCard automation={result} {...(onAction !== undefined ? { onAction } : {})} />
      );
    case 'dashboard_preview':
      return (
        <DashboardPreviewCard preview={result} {...(onAction !== undefined ? { onAction } : {})} />
      );
    default:
      return null;
  }
}

/**
 * A stable key per block.
 *
 * The server-issued identities are used where they exist (a plan id is unique
 * and stable across a re-render); everything else falls back to kind + position,
 * which is stable for a payload that does not change and is the honest amount
 * of identity a title-only block has.
 */
function keyFor(result: intelligence.IntelligenceResult, index: number): string {
  switch (result.kind) {
    case 'kpi':
      return `kpi:${result.metric}:${index}`;
    case 'automation':
    case 'dashboard_preview':
      return `${result.kind}:${result.planId}`;
    default:
      return `${result.kind}:${index}`;
  }
}
